import { useState, useCallback } from 'react'

const SEVERITY = {
  alta: { color: 'var(--gm-danger)', label: 'Alta' },
  media: { color: 'var(--accent)', label: 'Media' },
  baja: { color: 'var(--gm-success)', label: 'Baja' },
}

const HEALTH = {
  buena: { color: 'var(--gm-success)', label: 'Buena' },
  regular: { color: 'var(--accent)', label: 'Regular' },
  critica: { color: 'var(--gm-danger)', label: 'Crítica' },
}

/**
 * Panel de análisis con IA del dashboard.
 *
 * Envía las métricas ya agregadas a la Edge Function `dashboard-analysis`
 * (que llama a Claude Opus 4.8 con la clave en el servidor) y muestra el
 * resumen, los hallazgos priorizados y las recomendaciones.
 *
 * @param {object} props
 * @param {import('@supabase/supabase-js').SupabaseClient} props.supabase
 * @param {object} props.metrics - Métricas agregadas del dashboard.
 */
const AIAnalysisPanel = ({ supabase, metrics }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [analysis, setAnalysis] = useState(null)

  // El análisis se genera SOLO bajo demanda (botón), no al cargar la página.
  const generate = useCallback(async () => {
    if (!metrics) return
    setLoading(true)
    setError(null)
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        'dashboard-analysis',
        { body: { metrics } }
      )
      if (fnError) throw fnError
      if (data?.error) throw new Error(data.error)
      setAnalysis(data?.analysis || null)
    } catch (err) {
      console.error('Error análisis IA:', err)
      setError('No se pudo generar el análisis. Intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }, [supabase, metrics])

  const card = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  }
  // Los colores salen de los tokens del Design System: no hace falta
  // preguntar por el tema, el token ya vale lo que corresponde.
  const muted = 'var(--text-2)'
  const text = 'var(--text)'

  const health = analysis?.saludGeneral && HEALTH[analysis.saludGeneral]

  return (
    <div style={card}>
      {/* Encabezado */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: analysis || loading || error ? 14 : 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <i
            className="fa-solid fa-wand-magic-sparkles"
            style={{ color: 'var(--brand)', fontSize: 15 }}
          />
          <span style={{ fontSize: 14, fontWeight: 600, color: text }}>
            Análisis con IA
          </span>
          {health && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: health.color,
                background: `${health.color}1a`,
                borderRadius: 20,
                padding: '2px 10px',
              }}
            >
              Salud: {health.label}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={loading}
          style={{
            border: '1px solid var(--border)',
            background: 'transparent',
            color: muted,
            borderRadius: 8,
            padding: '5px 12px',
            fontSize: 12,
            fontWeight: 600,
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          <i
            className={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-rotate-right'} me-1`}
          />
          {loading ? 'Analizando…' : analysis ? 'Actualizar' : 'Generar'}
        </button>
      </div>

      {/* Estados */}
      {loading && !analysis && (
        <p style={{ color: muted, fontSize: 13, margin: 0 }}>
          Generando análisis a partir de los datos del dashboard…
        </p>
      )}

      {error && (
        <p style={{ color: 'var(--gm-danger)', fontSize: 13, margin: 0 }}>{error}</p>
      )}

      {!analysis && !loading && !error && (
        <p style={{ color: muted, fontSize: 13, margin: 0, lineHeight: 1.5 }}>
          Pulsa <strong>Generar</strong> para obtener un análisis con IA de los
          tickets: resumen, hallazgos y recomendaciones.
        </p>
      )}

      {analysis && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Resumen */}
          <p
            style={{ color: text, fontSize: 13.5, lineHeight: 1.5, margin: 0 }}
          >
            {analysis.resumen}
          </p>

          {/* Hallazgos */}
          {analysis.hallazgos?.length > 0 && (
            <div>
              <h4
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: muted,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  marginBottom: 8,
                }}
              >
                Hallazgos
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {analysis.hallazgos.map((h, i) => {
                  const sev = SEVERITY[h.severidad] || SEVERITY.baja
                  return (
                    <div key={i} style={{ display: 'flex', gap: 9 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: sev.color,
                          marginTop: 5,
                          flexShrink: 0,
                        }}
                      />
                      <div>
                        <span
                          style={{ fontSize: 13, fontWeight: 600, color: text }}
                        >
                          {h.titulo}
                        </span>
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: sev.color,
                            marginLeft: 8,
                            textTransform: 'uppercase',
                          }}
                        >
                          {sev.label}
                        </span>
                        <div
                          style={{
                            fontSize: 12.5,
                            color: muted,
                            lineHeight: 1.45,
                          }}
                        >
                          {h.detalle}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Recomendaciones */}
          {analysis.recomendaciones?.length > 0 && (
            <div>
              <h4
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: muted,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  marginBottom: 8,
                }}
              >
                Recomendaciones
              </h4>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {analysis.recomendaciones.map((r, i) => (
                  <li
                    key={i}
                    style={{
                      fontSize: 13,
                      color: text,
                      lineHeight: 1.5,
                      marginBottom: 4,
                    }}
                  >
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <span style={{ fontSize: 10.5, color: muted, fontStyle: 'italic' }}>
            Generado por IA · puede contener imprecisiones. Verifica antes de
            decidir.
          </span>
        </div>
      )}
    </div>
  )
}

export default AIAnalysisPanel
